import React, { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from "react";
import { detectSiteLocale, loadSiteMessages, type SiteLocale } from "./loadMessages";

type SiteI18nContextValue = {
  locale: SiteLocale;
  setLocale: (locale: SiteLocale) => void;
  t: (key: string) => string;
};

const STORAGE_KEY = "arbookplay-site-locale";

let localeSnapshot: SiteLocale = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as SiteLocale | null;
    if (stored === "en" || stored === "ko") return stored;
  } catch {
    /* ignore */
  }
  return detectSiteLocale();
})();

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function getSnapshot() {
  return localeSnapshot;
}

const SiteI18nContext = createContext<SiteI18nContextValue | null>(null);

export function SiteI18nProvider({ children }: { children: React.ReactNode }) {
  const locale = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setLocale = useCallback((next: SiteLocale) => {
    localeSnapshot = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    emit();
  }, []);

  const messages = useMemo(() => loadSiteMessages(locale), [locale]);

  const t = useCallback(
    (key: string) => messages[key] ?? key,
    [messages]
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <SiteI18nContext.Provider value={value}>{children}</SiteI18nContext.Provider>;
}

export function useSiteI18n() {
  const ctx = useContext(SiteI18nContext);
  if (!ctx) throw new Error("useSiteI18n must be used within SiteI18nProvider");
  return ctx;
}
